(function() {
  'use strict';
  
  // 設定オブジェクト（最適化済み）
  const CONFIG = {
    GROUP_TAG_PREFIX: 'group_',
    EXCLUDED_TAGS: ['gift', 'sale', 'new', 'featured', 'bestseller'],
    DEBUG: false,
    
    // パフォーマンス最適化設定
    DEBOUNCE_DELAY: 300,          // debounce遅延時間
    INTERSECTION_THRESHOLD: 0.1,   // IntersectionObserver閾値
    CACHE_DURATION: 5000          // キャッシュ有効期間（ms）
  };

  // デバッグログ機能（最適化済み）
  const debugLog = CONFIG.DEBUG ? 
    (message, data = null) => console.log('[CartGroupRestriction]', message, data || '') :
    () => {}; // DEBUG無効時は何もしない関数

  // ユーティリティ関数群
  const Utils = {
    // debounce関数（パフォーマンス最適化）
    debounce(func, delay) {
      let timeoutId;
      return function(...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(this, args), delay);
      };
    },

    // throttle関数（頻繁な更新制御）
    throttle(func, delay) {
      let lastExec = 0;
      return function(...args) {
        const now = Date.now();
        if (now - lastExec >= delay) {
          lastExec = now;
          return func.apply(this, args);
        }
      };
    },

    // 効率的な要素検索（キャッシュ機能付き）
    cachedQuerySelector(selector, context = document) {
      if (!this._selectorCache) this._selectorCache = new Map();
      
      const cacheKey = `${selector}:${context.tagName || 'document'}`;
      const cached = this._selectorCache.get(cacheKey);
      
      if (cached && cached.timestamp > Date.now() - CONFIG.CACHE_DURATION) {
        return cached.elements;
      }
      
      const elements = context.querySelectorAll(selector);
      this._selectorCache.set(cacheKey, {
        elements,
        timestamp: Date.now()
      });
      
      return elements;
    },

    // キャッシュクリア
    clearSelectorCache() {
      if (this._selectorCache) this._selectorCache.clear();
    }
  };

  // 最適化されたカート制限管理クラス
  class OptimizedCartGroupRestriction {
    constructor() {
      // 基本プロパティ
      this.currentCartGroup = null;
      this.currentPageGroup = null;
      this.isCartEmpty = true;
      this.addToCartButtons = [];
      this.isInitialized = false;

      // 個人情報同意機能
      this.privacyConsentSection = null;
      this.privacyConsentCheckbox = null;
      this.checkoutButtons = [];
      this.consentErrorMessage = null;
      this.checkoutHelpMessages = [];

      // 商品グループ設定（JSON設定から読み込み）
      this.productGroupSettingsData = null;
      this.groupSettings = null;

      // パフォーマンス最適化用プロパティ
      this.messageTemplate = null;
      this.intersectionObserver = null;
      this.updateThrottled = null;

      // バインドされたメソッド（メモリ効率化）
      this.boundUpdateState = this.updateState.bind(this);
      this.boundHandleVisibilityChange = this.handleVisibilityChange.bind(this);

      this.init();
    }

    async init() {
      try {
        debugLog('最適化版初期化開始');

        // 商品グループ設定をJSON DOMから読み込み
        this.loadGroupSettings();

        // テンプレート事前作成（DOM操作高速化）
        this.createMessageTemplate();

        // IntersectionObserver初期化
        this.initIntersectionObserver();

        // throttled関数作成
        this.updateThrottled = Utils.throttle(this.updateAddToCartButtons.bind(this), 100);

        // 非同期処理を並行実行
        await Promise.all([
          this.checkCurrentCartGroup(),
          this.checkCurrentPageGroup()
        ]);

        this.initializePrivacyConsent();
        this.initializeCoupon();
        this.updateAddToCartButtons();
        this.updateCouponSectionVisibility();
        this.isInitialized = true;

        debugLog('最適化版初期化完了', {
          cartGroup: this.currentCartGroup,
          pageGroup: this.currentPageGroup,
          isCartEmpty: this.isCartEmpty
        });
      } catch (e) {
        // 非同期初期化エラーが他のスクリプトに影響しないようにする
        if (Shopify.designMode) console.error('[CartGroupRestriction] init()エラー:', e);
      }
    }

    // 商品グループ設定をDOM JSONから読み込み
    loadGroupSettings() {
      const el = document.getElementById('product-group-settings');
      if (!el) {
        debugLog('product-group-settings JSONが見つかりません');
        return;
      }
      try {
        this.productGroupSettingsData = JSON.parse(el.textContent);
        this.groupSettings = new Map(
          (this.productGroupSettingsData.groups || []).map(g => [g.key, g])
        );
        debugLog('商品グループ設定を読み込み完了', { groupCount: this.groupSettings.size });
      } catch (e) {
        if (Shopify.designMode) console.error('商品グループ設定の読み込みに失敗:', e);
      }
    }

    // メッセージテンプレートをLiquid側の<template>から取得
    createMessageTemplate() {
      // Liquid側で定義済みの<template>を優先使用（HTML構造をLiquidに委譲）
      const liquidTemplate = document.getElementById('cart-restriction-message-template');
      if (liquidTemplate) {
        this.messageTemplate = liquidTemplate;
        debugLog('Liquidテンプレートを使用（cart-restriction-message-template）');
        return;
      }

      // フォールバック: Liquidテンプレートが見つからない場合のみJS生成
      if (Shopify.designMode) console.warn('[CartGroupRestriction] cart-restriction-message-template が見つかりません。JSフォールバックを使用します。');
      this.messageTemplate = document.createElement('template');
      const div = document.createElement('div');
      div.className = 'cart-restriction-message bg-[var(--color-error-container,#ffdad5)] border-2 border-[var(--color-error,#c54400)] rounded-[var(--shape-card,12px)] my-6 p-5 shadow-[var(--elevation-1,0_1px_2px_rgba(0,0,0,0.1))]';
      div.innerHTML = [
        '<div class="cart-restriction-content">',
        '<p class="restriction-title text-[length:var(--font-title-medium,1rem)] font-bold text-[var(--color-on-error-container,#410002)] mb-4 m-0"><strong>この商品は現在カートに追加できません</strong></p>',
        '<p class="mb-3 text-[var(--color-on-error-container,#410002)] m-0"><strong>現在のカート:</strong> <span class="current-group-name"></span></p>',
        '<p class="mb-3 text-[var(--color-on-error-container,#410002)] m-0"><strong>この商品:</strong> <span class="page-group-name"></span></p>',
        '<div class="cart-restriction-explanation mt-4 p-3 bg-black/5 rounded-[var(--shape-corner-medium,8px)] text-sm leading-normal text-[var(--color-on-error-container,#410002)]">配送・保存方法の関係で、異なるグループの商品は同時にご注文いただけません。</div>',
        '</div>',
        '<div class="cart-restriction-actions flex gap-4 mt-5 flex-wrap justify-center">',
        '<button type="button" class="btn-base clear-cart-btn btn-danger">カートを空にしてこの商品を購入</button>',
        '<a href="/cart" class="btn-base btn-primary inline-flex items-center justify-center">カートの商品を先に購入</a>',
        '</div>'
      ].join('');
      this.messageTemplate.content.appendChild(div);
      debugLog('JSフォールバックテンプレート作成完了');
    }

    // IntersectionObserver初期化（パフォーマンス最適化）
    initIntersectionObserver() {
      const options = {
        root: null,
        rootMargin: '50px',
        threshold: CONFIG.INTERSECTION_THRESHOLD
      };
      
      this.intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.processVisibleButton(entry.target);
          }
        });
      }, options);
    }

    // 可視ボタンの処理（遅延処理でパフォーマンス向上）
    processVisibleButton(button) {
      // 既に処理済みの場合はスキップ
      if (button.dataset.processed === 'true') return;
      
      // 処理済みマーク
      button.dataset.processed = 'true';
      
      // 必要に応じて制限メッセージを追加
      if (this.shouldRestrictButton(button)) {
        this.applyRestrictionToButton(button);
      }
    }

    // 商品のグループタグを取得（最適化済み）
    getProductGroup(productTags) {
      if (!productTags?.length) return null;

      // find操作を最適化
      for (const tag of productTags) {
        if (tag.startsWith(CONFIG.GROUP_TAG_PREFIX)) {
          const groupName = tag.replace(CONFIG.GROUP_TAG_PREFIX, '');
          if (!CONFIG.EXCLUDED_TAGS.includes(groupName)) {
            return groupName;
          }
        }
      }
      return null;
    }

    // 現在のカートのグループを確認（最適化済み）
    async checkCurrentCartGroup() {
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        
        debugLog('カートアイテム数:', cart.items.length);
        
        if (cart.items.length === 0) {
          this.isCartEmpty = true;
          this.currentCartGroup = null;
          debugLog('カートは空です');
          return;
        }

        this.isCartEmpty = false;
        
        // 最初の商品のグループを使用（効率化）
        const firstItem = cart.items[0];
        const productHandle = firstItem.handle || firstItem.product_handle;
        
        if (productHandle) {
          const productResponse = await fetch(`/products/${productHandle}.js`);
          const product = await productResponse.json();
          this.currentCartGroup = this.getProductGroup(product.tags);
        } else if (firstItem.product_tags) {
          this.currentCartGroup = this.getProductGroup(firstItem.product_tags);
        }
        
        debugLog('現在のカートグループ:', this.currentCartGroup);
      } catch (error) {
        if (Shopify.designMode) console.error('カートグループの確認に失敗:', error);
        this.currentCartGroup = null;
        this.isCartEmpty = true;
      }
    }

    // 現在のページの商品グループを確認（最適化済み）
    async checkCurrentPageGroup() {
      debugLog('ページグループ取得開始');
      
      // DOM取得を最適化
      const groupData = document.querySelector('.product-group-data');
      if (groupData) {
        try {
          const data = JSON.parse(groupData.textContent);
          if (data.group) {
            this.currentPageGroup = data.group;
            debugLog('product-group-dataから商品グループ取得:', this.currentPageGroup);
            return;
          }
        } catch (error) {
          debugLog('product-group-dataからの取得に失敗:', error);
        }
      }

      // URLから商品ハンドルを取得（最適化済み）
      const pathParts = window.location.pathname.split('/');
      const productsIndex = pathParts.indexOf('products');
      
      if (productsIndex !== -1 && pathParts.length > productsIndex + 1) {
        const productHandle = pathParts[productsIndex + 1];
        if (productHandle) {
          await this.fetchCurrentPageGroup(productHandle);
        }
      }

      debugLog('ページグループ取得完了:', this.currentPageGroup);
    }

    // 現在のページの商品グループを非同期取得（最適化済み）
    async fetchCurrentPageGroup(productHandle) {
      try {
        debugLog('商品情報を非同期取得開始:', productHandle);
        const productResponse = await fetch(`/products/${productHandle}.js`);
        
        if (!productResponse.ok) {
          debugLog('商品情報の取得に失敗:', productResponse.status);
          return;
        }
        
        const product = await productResponse.json();
        this.currentPageGroup = this.getProductGroup(product.tags);
        debugLog('非同期取得した現在のページグループ:', this.currentPageGroup);
        
        // 初期化完了後の場合はボタンを更新
        if (this.isInitialized) {
          this.updateAddToCartButtons();
        }
      } catch (error) {
        if (Shopify.designMode) console.error('商品情報の取得に失敗:', error);
      }
    }

    // ボタン制限判定（効率化）
    shouldRestrictButton(button) {
      // 販売期間チェックが優先
      if (window.productSalesPeriodManager && !window.productSalesPeriodManager.isSalesPeriodActive()) {
        return false; // 販売期間制限が優先されるため、カートグループ制限は適用しない
      }

      // カートが空の場合は制限なし
      if (this.isCartEmpty) return false;

      // 同じグループの場合は制限なし
      if (this.currentCartGroup === this.currentPageGroup) return false;

      // 異なるグループの場合は制限
      return true;
    }

    // カートに追加ボタンの状態を更新（大幅最適化）
    updateAddToCartButtons() {
      // 販売期間チェックを最優先で実行
      if (window.productSalesPeriodManager && !window.productSalesPeriodManager.isSalesPeriodActive()) {
        debugLog('判定: 販売期間外のため制限適用（カートグループ制限より優先）');
        this.removeExistingRestrictionMessages();
        return;
      }
    
      // ボタン要素を効率的に取得（キャッシュ活用）
      this.addToCartButtons = Array.from(Utils.cachedQuerySelector(
        'button[name="add"], button[type="submit"][form*="product"], .product-form__cart, [data-cart-add], .product-form__submit'
      ));
    
      debugLog('ボタン状態更新開始', {
        buttonCount: this.addToCartButtons.length,
        cartGroup: this.currentCartGroup,
        pageGroup: this.currentPageGroup,
        isCartEmpty: this.isCartEmpty
      });
    
      // 既存の制限メッセージを効率的に削除
      this.removeExistingRestrictionMessages();
    
      // カートが空の場合のみ制限なし
      if (this.isCartEmpty) {
        this.enableAllButtons();
        debugLog('判定: カートが空のため制限なし');
        return;
      }
    
      // 同じグループの場合は制限なし
      if (this.currentCartGroup === this.currentPageGroup) {
        this.enableAllButtons();
        debugLog('判定: 同じグループのため制限なし');
        return;
      }
    
      // 異なるグループの場合は制限
      this.disableButtonsWithMessage();
      debugLog('判定: 異なるグループのため制限適用');

      // 個人情報同意表示・クーポン表示も更新
      this.updatePrivacyConsentDisplay();
      this.updateCouponSectionVisibility();
    }

    // すべてのボタンを有効化（元の無効状態を保持）
    enableAllButtons() {
      // requestAnimationFrameで描画を最適化
      requestAnimationFrame(() => {
        this.addToCartButtons.forEach(button => {
          // カートグループ制限で無効化したボタンのみ復元する
          if (button.dataset.disabledByGroupRestriction !== 'true') return;

          button.disabled = false;
          button.style.opacity = '';
          button.style.cursor = '';

          // aria属性をリセット
          button.removeAttribute('aria-disabled');
          button.removeAttribute('aria-describedby');
          button.dataset.processed = 'false';
          button.dataset.disabledByGroupRestriction = 'false';
        });
      });

      debugLog('カートグループ制限で無効化したボタンを復元しました');
    }

    // ボタンを無効化して制限メッセージを表示（最適化済み）
    disableButtonsWithMessage() {
      // 販売期間制限が優先される場合はスキップ
      if (this.checkSalesPeriodRestriction()) {
        debugLog('販売期間制限が優先されるため、カートグループ制限メッセージはスキップ');
        return;
      }

      // requestAnimationFrameでバッチ処理
      requestAnimationFrame(() => {
        this.addToCartButtons.forEach((button, index) => {
          // 販売期間で無効化されているボタンはスキップ
          if (button.getAttribute('aria-describedby') === 'sales-period-restriction') {
            debugLog('ボタンは既に販売期間制限されているためスキップ:', button);
            return;
          }

          this.applyRestrictionToButton(button, index);
        });
      });

      debugLog('カート追加ボタンを無効化し、制限メッセージを表示しました');
    }

    // 個別ボタンに制限を適用
    applyRestrictionToButton(button, index = 0) {
      // ボタンを無効化（マーキングして復元可能にする）
      button.dataset.disabledByGroupRestriction = 'true';
      button.disabled = true;
      button.style.opacity = '0.5';
      button.style.cursor = 'not-allowed';

      // アクセシビリティ対応
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-describedby', `cart-restriction-message-${index}`);

      // 制限メッセージを作成
      this.createRestrictionMessage(button, index);
    }

    // 制限メッセージを作成（大幅最適化・innerHTML削除）
    createRestrictionMessage(button, index) {
      const messageId = `cart-restriction-message-${index}`;
      
      // 既存のメッセージがあれば削除
      const existingMessage = document.getElementById(messageId);
      if (existingMessage) {
        existingMessage.remove();
      }

      // テンプレートから高速クローン（innerHTML不使用）
      const messageClone = this.messageTemplate.content.cloneNode(true);
      const messageDiv = messageClone.querySelector('.cart-restriction-message');
      messageDiv.id = messageId;

      // テキスト更新のみ（DOM操作最小化）
      const currentGroupSpan = messageClone.querySelector('.current-group-name');
      const pageGroupSpan = messageClone.querySelector('.page-group-name');
      
      // グループ名を効率的に設定
      const currentGroupName = this.formatGroupDisplayName(this.currentCartGroup);
      const pageGroupName = this.formatGroupDisplayName(this.currentPageGroup);
      
      currentGroupSpan.textContent = currentGroupName;
      pageGroupSpan.textContent = pageGroupName;

      // イベントリスナーを効率的に設定
      const clearBtn = messageClone.querySelector('.clear-cart-btn');
      clearBtn.addEventListener('click', this.clearCartAndReload.bind(this), { 
        once: true,    // 一度だけ実行
        passive: false // preventDefault使用可能
      });

      // DocumentFragmentを使用して効率的に挿入
      button.parentNode.insertBefore(messageClone, button.nextSibling);
    }

    // グループ名表示用フォーマット（product-group-settings JSON設定から動的に取得）
    formatGroupDisplayName(groupName) {
      if (!groupName) return '通常発送';

      // JSON設定から動的に取得
      const group = this.groupSettings?.get(groupName);
      if (group && group.displayName) {
        return `${group.displayName}発送`;
      }

      // JSON設定に未登録の場合はgroupNameをそのまま使用
      return `${groupName}発送`;
    }

    // グループ名を表示用にフォーマット（レガシー互換性維持）
    formatGroupName(groupName) {
      return this.formatGroupDisplayName(groupName);
    }

    // 既存の制限メッセージを削除（最適化済み）
    removeExistingRestrictionMessages() {
      // 効率的な一括削除
      const existingMessages = Utils.cachedQuerySelector('.cart-restriction-message');
      if (existingMessages.length > 0) {
        requestAnimationFrame(() => {
          existingMessages.forEach(message => message.remove());
        });
      }
    }

    // 販売期間制限チェック（最適化済み）
    checkSalesPeriodRestriction() {
      return window.productSalesPeriodManager ? 
        !window.productSalesPeriodManager.isSalesPeriodActive() : false;
    }

    // 状態更新（debounce適用・個人情報同意・クーポン対応）
    updateState = Utils.debounce(async function() {
      debugLog('状態更新開始');
      await Promise.all([
        this.checkCurrentCartGroup(),
        this.checkCurrentPageGroup()
      ]);
      this.updateAddToCartButtons();
      this.updatePrivacyConsentDisplay();
      this.updateCouponSectionVisibility();
      debugLog('状態更新完了', {
        cartGroup: this.currentCartGroup,
        isCartEmpty: this.isCartEmpty
      });
    }, CONFIG.DEBOUNCE_DELAY);

    // ページ可視性変更ハンドラー
    handleVisibilityChange() {
      if (!document.hidden && this.isInitialized) {
        this.updateThrottled();
      }
    }

    // カートをクリアしてページをリロード（最適化済み）
    async clearCartAndReload() {
      try {
        // カートクリア処理（効率化）
        const clearResponse = await fetch('/cart/clear.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (clearResponse.ok) {
          // スクリーンリーダー用アナウンス
          this.announceToScreenReader('カートを空にしました');
          
          // 少し遅延してからリロード（UX向上）
          setTimeout(() => {
            window.location.reload();
          }, 300);
        } else {
          throw new Error('カートのクリアに失敗しました');
        }
      } catch (error) {
        if (Shopify.designMode) if (Shopify.designMode) console.error('カートクリア処理に失敗:', error);
        // フォールバック：直接リロード
        window.location.reload();
      }
    }

    // 個人情報同意機能初期化（動的グループ判定・管理画面設定対応）
    initializePrivacyConsent() {
      debugLog('個人情報同意機能の初期化開始');

      // main-cart-footer.liquidの実装に合わせたDOM要素取得
      this.privacyConsentSection = document.getElementById('cart-privacy-consent');
      this.privacyConsentCheckbox = document.getElementById('privacy-consent-checkbox');
      this.consentErrorMessage = document.getElementById('consent-error-message');

      // チェックアウトボタンを効率的に取得
      this.checkoutButtons = Array.from(Utils.cachedQuerySelector(
        '#checkout, .cart__checkout-button, [name="checkout"]'
      ));

      if (!this.privacyConsentSection) {
        debugLog('個人情報同意セクションが見つかりません');
        return;
      }

      if (this.privacyConsentCheckbox) {
        // チェックボックスのイベントリスナーを最適化して追加
        this.privacyConsentCheckbox.addEventListener('change', () => {
          this.updateCheckoutButtonState();
          this.hideConsentError();
          this.updateCartAttributes();
        }, { passive: true });

        debugLog('個人情報同意チェックボックスのイベントリスナーを設定');
      }

      // チェックアウトボタンのクリックイベントを最適化
      this.checkoutButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          if (this.shouldShowPrivacyConsent() && !this.isConsentGiven()) {
            e.preventDefault();
            e.stopImmediatePropagation();
            this.showConsentError();
            this.focusConsentCheckbox();
            return false;
          }
        }, { passive: false });
      });

      // 初期状態を更新
      this.updatePrivacyConsentDisplay();

      debugLog('個人情報同意機能の初期化完了');
    }

    // 個人情報同意が必要かどうか動的に判定
    shouldShowPrivacyConsent() {
      if (this.isCartEmpty) return false;

      // JSON設定から判定（product-group-settings のrequiresPrivacyConsent）
      const config = this.groupSettings?.get(this.currentCartGroup);
      return config?.requiresPrivacyConsent === true;
    }

    // クーポン機能初期化
    initializeCoupon() {
      const applyBtn = document.getElementById('apply-coupon-btn');
      const couponInput = document.getElementById('coupon-code-input');
      const couponFeedback = document.getElementById('coupon-feedback');

      if (!applyBtn || !couponInput) {
        debugLog('クーポン入力要素が見つかりません');
        return;
      }

      applyBtn.addEventListener('click', () => {
        const code = couponInput.value.trim();
        if (!code) {
          if (couponFeedback) {
            couponFeedback.textContent = 'クーポンコードを入力してください。';
            couponFeedback.className = 'mt-2 text-body-medium text-error';
          }
          return;
        }

        // チェックアウトURLにdiscountパラメータを付与するようcart formを更新
        this.applyCouponCode(code, couponFeedback);
      }, { passive: true });

      // Enterキーで適用
      couponInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          applyBtn.click();
        }
      });

      debugLog('クーポン機能の初期化完了');
    }

    // クーポンコードを適用（checkout URLにdiscount付与）
    applyCouponCode(code, feedbackEl) {
      // カートフォームのaction URLにdiscountパラメータを付与
      const cartForm = document.getElementById('cart');
      if (cartForm) {
        const currentAction = cartForm.getAttribute('action') || '/cart';
        const baseAction = currentAction.split('?')[0];
        cartForm.setAttribute('action', `${baseAction}?discount=${encodeURIComponent(code)}`);
      }

      // チェックアウトボタンにもdiscountを付与
      const checkoutBtn = document.getElementById('checkout');
      if (checkoutBtn) {
        checkoutBtn.setAttribute('formaction', `/checkout?discount=${encodeURIComponent(code)}`);
      }

      if (feedbackEl) {
        // Liquid側の<template>を優先使用（innerHTML不使用）
        const couponTemplate = document.getElementById('coupon-feedback-template');
        if (couponTemplate) {
          const clone = couponTemplate.content.cloneNode(true);
          const codeDisplay = clone.querySelector('.coupon-code-display');
          if (codeDisplay) codeDisplay.textContent = code;
          feedbackEl.textContent = '';
          feedbackEl.appendChild(clone);
        }
        feedbackEl.className = 'mt-2 text-body-medium';
      }

      debugLog('クーポンコード適用:', code);
    }

    // クーポンセクションの表示/非表示を動的に更新
    updateCouponSectionVisibility() {
      const couponSection = document.getElementById('cart-coupon-section');
      if (!couponSection) return;

      let showCoupon;
      if (this.isCartEmpty || !this.currentCartGroup) {
        showCoupon = this.productGroupSettingsData?.defaultShowCouponInput !== false;
      } else {
        const groupConfig = this.groupSettings?.get(this.currentCartGroup);
        showCoupon = groupConfig
          ? groupConfig.showCouponInput !== false
          : this.productGroupSettingsData?.defaultShowCouponInput !== false;
      }

      couponSection.style.display = showCoupon ? '' : 'none';
      debugLog('クーポンセクション表示状態を更新:', showCoupon);
    }

    // 個人情報同意表示を動的に更新
    updatePrivacyConsentDisplay() {
      const shouldShow = this.shouldShowPrivacyConsent();
      const privacyConsent = document.getElementById('cart-privacy-consent');

      if (privacyConsent) {
        const consentContent = privacyConsent.querySelector('.bg-warning-container');
        if (consentContent) {
          consentContent.style.display = shouldShow ? '' : 'none';
        }
      }

      this.updateCheckoutButtonState();
      debugLog('個人情報同意表示状態を更新:', shouldShow);
    }

    // HTML エスケープ
    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    // 個人情報同意が得られているかチェック（新規メソッド）
    isConsentGiven() {
      return this.privacyConsentCheckbox && this.privacyConsentCheckbox.checked;
    }

    // チェックアウトボタンの状態を更新（動的判定対応）
    updateCheckoutButtonState() {
        console.log(this.shouldShowPrivacyConsent());
      if (!this.shouldShowPrivacyConsent()) {
        console.log(this.shouldShowPrivacyConsent());
        this.enableCheckoutButtons();
        return;
      }

      if (this.isConsentGiven()) {
        this.enableCheckoutButtons();
        debugLog('同意取得済み：チェックアウトボタンを有効化');
      } else {
        this.disableCheckoutButtons();
        debugLog('同意未取得：チェックアウトボタンを無効化');
      }
    }

    // 同意エラー表示（main-cart-footer準拠・最適化済み）
    showConsentError() {
      if (this.consentErrorMessage) {
        this.consentErrorMessage.style.display = 'block';
        this.consentErrorMessage.setAttribute('aria-live', 'assertive');
        
        // スクリーンリーダー用のアナウンス
        this.announceToScreenReader('個人情報提供への同意が必要です。チェックボックスをチェックしてください。');
        
        debugLog('同意エラーメッセージを表示');
      }
    }

    // 同意エラー非表示（main-cart-footer準拠・最適化済み）
    hideConsentError() {
      if (this.consentErrorMessage) {
        this.consentErrorMessage.style.display = 'none';
        this.consentErrorMessage.removeAttribute('aria-live');
        debugLog('同意エラーメッセージを非表示');
      }
    }

    // 同意チェックボックスにフォーカス（新規メソッド）
    focusConsentCheckbox() {
      if (this.privacyConsentCheckbox) {
        this.privacyConsentCheckbox.focus();
        
        // チェックボックスまでスクロール
        this.privacyConsentCheckbox.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        });
      }
    }

    // カート属性を更新（同意状況を保存・動的判定対応）
    updateCartAttributes() {
      if (this.shouldShowPrivacyConsent() && this.isConsentGiven()) {
        const consentLabel = this.productGroupSettingsData?.privacyConsent?.label || '同意する';
        const formData = new FormData();
        formData.append('attributes[個人情報提供同意]', consentLabel);
        
        fetch('/cart/update.js', {
          method: 'POST',
          body: formData
        }).then(response => response.json())
        .then(data => {
          debugLog('個人情報提供同意をカート属性に保存');
        })
        .catch(error => {
          if (Shopify.designMode) console.error('カート属性の更新エラー:', error);
        });
      }
    }

    // チェックアウトヘルプメッセージを表示（新規メソッド）
    showCheckoutHelpMessage(button, index) {
      const helpId = `checkout-help-message-${index}`;
      
      // 既存のヘルプメッセージがあれば削除
      const existingHelp = document.getElementById(helpId);
      if (existingHelp) {
        existingHelp.remove();
      }

      // ヘルプメッセージ要素を作成
      const helpMessage = document.createElement('div');
      helpMessage.id = helpId;
      helpMessage.className = 'checkout-help-message has-text-danger mt-0 mb-2 is-size-5 has-text-centered';
      helpMessage.textContent = '個人情報提供への同意にチェックをしてください';

      // ボタンの親要素に挿入
      button.parentNode.insertBefore(helpMessage, button.nextSibling);
      
      // ヘルプメッセージを管理配列に追加
      this.checkoutHelpMessages.push(helpMessage);

      debugLog('チェックアウトヘルプメッセージを表示:', helpId);
    }

    // チェックアウトヘルプメッセージを非表示（新規メソッド）
    hideCheckoutHelpMessage(button, index) {
      const helpId = `checkout-help-message-${index}`;
      const helpMessage = document.getElementById(helpId);
      
      if (helpMessage) {
        helpMessage.remove();
        
        // 管理配列からも削除
        const helpIndex = this.checkoutHelpMessages.indexOf(helpMessage);
        if (helpIndex > -1) {
          this.checkoutHelpMessages.splice(helpIndex, 1);
        }
        
        debugLog('チェックアウトヘルプメッセージを非表示:', helpId);
      }
    }

    // すべてのチェックアウトヘルプメッセージを削除（新規メソッド）
    hideAllCheckoutHelpMessages() {
      this.checkoutHelpMessages.forEach(helpMessage => {
        if (helpMessage.parentNode) {
          helpMessage.remove();
        }
      });
      this.checkoutHelpMessages = [];
      debugLog('すべてのチェックアウトヘルプメッセージを削除');
    }

    // チェックアウトボタン無効化（修正版）
    disableCheckoutButtons() {
      this.checkoutButtons.forEach((button, index) => {
        button.disabled = true;
        button.classList.add('checkout-button-disabled');
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('aria-describedby', `checkout-help-message-${index}`);
        
        // ヘルプメッセージを表示（ボタンテキスト変更は削除）
        this.showCheckoutHelpMessage(button, index);
      });
    }

    // チェックアウトボタン有効化（修正版）
    enableCheckoutButtons() {
      this.checkoutButtons.forEach((button, index) => {
        button.disabled = false;
        button.classList.remove('checkout-button-disabled');
        button.removeAttribute('aria-disabled');
        button.removeAttribute('aria-describedby');
        
        // ヘルプメッセージを非表示（ボタンテキスト復元は削除）
        this.hideCheckoutHelpMessage(button, index);
      });
    }

    // スクリーンリーダー用アナウンス（最適化済み）
    announceToScreenReader(message) {
      const announcement = document.createElement('div');
      announcement.setAttribute('aria-live', 'polite');
      announcement.setAttribute('aria-atomic', 'true');
      announcement.className = 'sr-only';
      announcement.style.position = 'absolute';
      announcement.style.left = '-10000px';
      announcement.style.width = '1px';
      announcement.style.height = '1px';
      announcement.style.overflow = 'hidden';
      announcement.textContent = message;
      
      document.body.appendChild(announcement);
      
      // 自動削除
      setTimeout(() => {
        if (announcement.parentNode) {
          announcement.parentNode.removeChild(announcement);
        }
      }, 1000);
    }

    // クリーンアップ（メモリリーク防止・個人情報同意対応）
    destroy() {
      // IntersectionObserver停止
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }

      // イベントリスナー削除
      if (this.privacyConsentCheckbox) {
        this.privacyConsentCheckbox.removeEventListener('change', this.updatePrivacyConsentDisplay);
      }

      // ヘルプメッセージをクリーンアップ
      this.hideAllCheckoutHelpMessages();

      // キャッシュクリア
      Utils.clearSelectorCache();

      // 参照クリア
      this.addToCartButtons = [];
      this.checkoutButtons = [];
      this.checkoutHelpMessages = [];
      this.messageTemplate = null;
      this.privacyConsentSection = null;
      this.privacyConsentCheckbox = null;
      this.consentErrorMessage = null;
      
      debugLog('リソースをクリーンアップしました');
    }
  }

  // グローバルインスタンス
  let cartGroupRestriction = null;

  // 最適化された初期化関数
  function initCartGroupRestriction() {
    try {
      // 既存インスタンスがあればクリーンアップ
      if (cartGroupRestriction) {
        cartGroupRestriction.destroy();
      }

      cartGroupRestriction = new OptimizedCartGroupRestriction();

      // グローバルアクセス用
      window.cartGroupRestriction = cartGroupRestriction;

      debugLog('最適化版カートグループ制限機能を初期化しました');
    } catch (e) {
      // 初期化エラーが他のスクリプト（product-form.js等）に影響しないようにする
      if (Shopify.designMode) console.error('[CartGroupRestriction] 初期化エラー:', e);
    }
  }

  // 効率的な初期化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartGroupRestriction, { once: true });
  } else {
    initCartGroupRestriction();
  }

  // 最適化されたイベントリスナー
  document.addEventListener('cart:updated', () => {
    debugLog('カート更新イベントを検出');
    if (cartGroupRestriction) {
      cartGroupRestriction.updateState();
    }
  }, { passive: true });

  // ページ可視性API活用
  document.addEventListener('visibilitychange', () => {
    if (cartGroupRestriction) {
      cartGroupRestriction.handleVisibilityChange();
    }
  }, { passive: true });

  // 最適化されたページ変遷検出
  let currentPath = window.location.pathname;
  const pathChangeInterval = setInterval(() => {
    if (window.location.pathname !== currentPath) {
      const newPath = window.location.pathname;
      debugLog('ページ変遷を検出:', newPath);
      
      if (cartGroupRestriction) {
        if (newPath.includes('/products/')) {
          debugLog('商品ページへの移動を検出、グループをリセットして再初期化');
          setTimeout(async () => {
            cartGroupRestriction.currentPageGroup = null;
            await cartGroupRestriction.checkCurrentPageGroup();
            cartGroupRestriction.updateAddToCartButtons();
          }, 100);
        } else {
          debugLog('商品ページ以外への移動、制限をクリア');
          cartGroupRestriction.removeExistingRestrictionMessages();
          cartGroupRestriction.enableAllButtons();
        }
      }
      
      currentPath = newPath;
    }
  }, 1000);

  // ページアンロード時のクリーンアップ
  window.addEventListener('beforeunload', () => {
    clearInterval(pathChangeInterval);
    if (cartGroupRestriction) {
      cartGroupRestriction.destroy();
    }
  }, { once: true });

})();